import React from 'react';
import { Stack } from '@fluentui/react';
import { useStyles } from './index.styles';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';
import { EntityTagInput } from 'common/types/globalTypes';
import {
  invoiceSigningAvailableTags,
  invoiceSigningAvailableTagsVariables,
} from 'common/graphql/__generated__/InvoiceSignAvailTags';
import {
  GetInvoiceTags,
  GetInvoiceTagsVariables,
} from 'common/graphql/__generated__/GetInvoiceTags';
import {
  EntityTagInvoiceSignCreate,
  EntityTagInvoiceSignCreateVariables,
} from 'common/graphql/__generated__/EntityTagInvoiceSignCreate';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';

import ADD_NEW_TAG from '../../../../../../graphql/EntityTagInvoiceSignCreate.graphql';
import AVAILABLE_TAGS_LIST from '../../../../../../graphql/InvoiceSignAvailTags.graphql';
import INVOICE_TAGS_LIST from '../../../../../../graphql/GetInvoiceTags.graphql';
import ENTITY_TAG_DELETE from '../../../../../../graphql/EntityTagDelete.graphql';

interface TagsProps {
  invoiceId: string;
}

export const Tags: React.FC<TagsProps> = ({ invoiceId }) => {
  const styles = useStyles();

  // Memoized functions for extracting data and generating variables
  const extractAvailableTags = React.useCallback(
    (data: invoiceSigningAvailableTags | undefined) =>
      data?.invoiceSigningAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? [],
    []
  );

  const extractEntityTags = React.useCallback(
    (data: GetInvoiceTags | undefined) =>
      data?.invoice?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown',
      })) ?? [],
    []
  );

  const getAvailableTagsVars = React.useCallback(
    (entityId: string): invoiceSigningAvailableTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const getEntityTagsVars = React.useCallback(
    (entityId: string): GetInvoiceTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const createTagsVars = React.useCallback(
    (tags: EntityTagInput[]): EntityTagInvoiceSignCreateVariables => ({
      input: {
        entityTag: tags,
      },
    }),
    []
  );

  const deleteTagsVars = React.useCallback(
    (id: string, rowTimestamp: string): EntityTagDeleteVariables => ({
      entityTagDeleteInput: {
        entityDelete: [{ id, rowTimestamp }],
      },
    }),
    []
  );

  return (
    <Stack tokens={{ childrenGap: 15, padding: '24px 25px 0px' }}>
      <EntityTagsSection<
        invoiceSigningAvailableTags,
        invoiceSigningAvailableTagsVariables,
        GetInvoiceTags,
        GetInvoiceTagsVariables,
        EntityTagInvoiceSignCreate,
        EntityTagInvoiceSignCreateVariables,
        EntityTagDelete,
        EntityTagDeleteVariables
      >
        entityId={invoiceId}
        getAvailableTagsGql={AVAILABLE_TAGS_LIST}
        getEntityTagsGql={INVOICE_TAGS_LIST}
        addTagGql={ADD_NEW_TAG}
        deleteTagGql={ENTITY_TAG_DELETE}
        extractAvailableTags={extractAvailableTags}
        extractEntityTags={extractEntityTags}
        getAvailableTagsVars={getAvailableTagsVars}
        getEntityTagsVars={getEntityTagsVars}
        createTagsVars={createTagsVars}
        deleteTagsVars={deleteTagsVars}
      />
    </Stack>
  );
};
